import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const LINES = [
  { text: "Brands", accent: false },
  { text: "worth", accent: false },
  { text: "building.", accent: true },
];

const ENTER = {
  hidden: { y: "105%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.15,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.25 + i * 0.14,
    },
  }),
};

export default function KineticHero({ onStartProject }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImage = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] pt-40 md:pt-48 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero"
    >
      {/* meta strip */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-14 md:mb-24 text-[10px] md:text-[11px] font-mono uppercase tracking-[0.24em] text-stone-muted"
          data-testid="hero-meta"
        >
          <span>[ Vol. XI · 2025 ]</span>
          <span className="hidden md:block">Boutique brand consultancy</span>
          <span className="hidden md:flex items-center gap-2">
            <span className="status-dot" />
            Available — Q3 2026
          </span>
          <span className="md:text-right">Siliguri · India</span>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 items-end">
          {/* Kinetic type */}
          <div className="col-span-12 lg:col-span-8">
            <h1
              className="font-display text-[19vw] md:text-[13vw] lg:text-[11.5vw] leading-[0.86] tracking-tightest text-charcoal"
              aria-label="Brands worth building."
              data-testid="hero-headline"
            >
              {LINES.map((l, i) => (
                <span key={i} className="mask-line">
                  <motion.span
                    className="mask-line-inner"
                    variants={ENTER}
                    initial="hidden"
                    animate="show"
                    custom={i}
                  >
                    {l.accent ? (
                      <em className="italic font-normal text-stone-muted">{l.text}</em>
                    ) : (
                      l.text
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>
          </div>

          {/* Parallax photograph */}
          <div className="col-span-12 lg:col-span-4 flex justify-end">
            <div className="w-full md:w-[70%] lg:w-full frame overflow-hidden">
              <motion.div style={{ y: yImage, scale }} className="relative">
                <img
                  src="https://images.pexels.com/photos/36339062/pexels-photo-36339062.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Editorial packaging"
                  className="w-full aspect-[3/4] object-cover"
                  loading="eager"
                />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-14 md:mt-24 grid grid-cols-12 gap-6 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-6 lg:col-span-5 text-lg md:text-xl leading-relaxed text-charcoal/80 font-serif italic"
            data-testid="hero-sub"
          >
            A boutique brand consultancy for founders who want a
            &nbsp;strategy their designer can actually draw &mdash;&nbsp;and a design their board will actually sign off.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-6 lg:col-span-4 lg:col-start-9 flex flex-col md:flex-row md:items-end gap-4"
          >
            <button
              onClick={onStartProject}
              data-testid="hero-start-project"
              className="inline-flex items-center justify-center gap-3 border border-charcoal bg-charcoal text-ivory px-7 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-ivory hover:text-charcoal transition-colors duration-500"
            >
              Start a project
              <span className="text-base">↗</span>
            </button>
            <a
              href="/work"
              className="u-link inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] py-4"
              data-testid="hero-view-work"
            >
              View the work
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-6 md:left-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-stone-muted"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="inline-block h-4 w-px bg-charcoal/40"
        />
      </motion.div>
    </section>
  );
}
